/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Granite Bagas
*/

import React, { useMemo, useState } from 'react';
import Toast from 'react-native-toast-message';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useDispatch } from 'react-redux';

import * as Layout from '@components/screen/Layout';
import SelectBox from '@components/form/SelectBox';
import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';
import RenderIf from '@components/global/RenderIf';
import ModalFull from '@components/modal/ModalFull';
import Table from '@components/table/Table';
import Image from '@components/global/Image';

import { isWeb } from '@global/screens';
import { FILTER_YEARS } from '@/constants';
import Colors from '@global/colors';
import { MsOrder, deleteForecaseEstimation } from '@api/ms-order';
import { filterAdded } from '@/store/params';
import { hasNoAccess } from '@global/helpers';
import { showAlert } from '@/store/global';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetForecastStockListQuery,
  useGetForecastStockCountQuery,
  useGetForecastStockInfiniteListInfiniteQuery,
} from '@api/listApi';

const CURRENT_YEAR = new Date().getFullYear();

const GROUP_BY_OPTIONS = [
  { label: 'Account', value: 'account' },
  { label: 'Sales Team', value: 'sales_team' },
];

const TABLE_STRUCTURES = {
  team_name: {
    head: 'Sales Team',
    body: 'name',
  },
  created_by: {
    head: 'Created By',
    body: 'created_by.name',
  },
  last_updated: {
    head: 'Last Updated By',
    body: 'updated_by.name',
  },
  action: {
    head: 'Action',
    body: 'action',
    type: 'slot',
  },
};

// Search box writes `name` (→ $regex); the year is always sent (defaults to the
// current year) so it survives mount / search / reset.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  filter.year = raw.year || CURRENT_YEAR;
  return filter;
};

const ForecastStockScreen = (props) => {
  const dispatch = useDispatch();

  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetForecastStockListQuery,
    countHook: useGetForecastStockCountQuery,
    infiniteHook: useGetForecastStockInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  const [selectedGroupBy, setSelectedGroupBy] = useState(null);
  const [showModalExport, setShowModalExport] = useState(false);

  const hasAccessEdit = !hasNoAccess([
    'superadmin',
    'general',
    'sales_admin',
    'branch_head',
    'sales_supervisor',
  ]);

  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const filterYear = params.filter?.year ?? CURRENT_YEAR;

  const rows = useMemo(
    () => (Array.isArray(list.data) ? list.data : []),
    [list.data],
  );

  const onSelectYear = (value) => {
    dispatch(
      filterAdded({
        key: scope,
        filter: { ...params.filter, year: value.value },
      }),
    );
  };

  const handleExportModal = () => {
    setShowModalExport(true);
    setSelectedGroupBy(null);
  };

  const handleCloseExportModal = () => {
    setShowModalExport(false);
    setSelectedGroupBy(null);
  };

  const handleExportForecast = async () => {
    try {
      await MsOrder.exportForecastStock({
        group_by: selectedGroupBy.value,
        year: filterYear,
      });
      Toast.show({
        text1: 'Export in progress, the result will be sent to your email',
        type: 'success',
      });
    } catch (error) {
      Toast.show({ text1: 'A system error occurred.', type: 'error' });
    }
    handleCloseExportModal();
  };

  const handleDelete = async (data) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Delete Forecast',
        description: 'Are you sure want to delete this forecast data?',
        onCallback: async () => {
          await deleteForecaseEstimation(data._id, filterYear);
          list.refetch();
          Toast.show({ text1: 'Forecast Deleted', type: 'success' });
        },
      }),
    );
  };

  const handleDetail = (data) => {
    openChild('ForecastStockDetailScreen', {
      id: data?._id,
      title: data?.name,
      year: filterYear,
    });
  };

  return (
    <Layout.Container>
      <RenderIf condition={isWeb}>
        <Layout.Title title="Forecast Stock" />
        <View style={styles.tabContainer}>
          <Button
            type="primary"
            rounded={true}
            buttonStyle={{ width: 200 }}
            title={
              <SociollaText
                type="medium"
                style={{ fontSize: 14, color: Colors.white }}>
                Export
              </SociollaText>
            }
            onPress={handleExportModal}
          />
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <SociollaText style={{ color: Colors.grey1, marginRight: 15 }}>
              Sales Year
            </SociollaText>
            <View>
              <SelectBox
                label="Year"
                hideLabel
                options={FILTER_YEARS}
                value={
                  FILTER_YEARS.find((y) => y.value === filterYear) ||
                  FILTER_YEARS[0]
                }
                placeholder="Select Year"
                trackBy="value"
                labelKey="label"
                isDropdown={isWeb}
                onChange={onSelectYear}
              />
            </View>
          </View>
        </View>

        <Table
          name="forecast-stock"
          filterName={scope}
          searchKey="name"
          searchPlaceholder="Search by name"
          searchValue={searchValue}
          structures={TABLE_STRUCTURES}
          data={rows}
          loading={list.isLoading}
          refetching={list.isFetching}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          hasNoHide
          showFilter={false}
          hasCallback={true}
          onClickRow={({ data }) => handleDetail(data)}
          slot={{
            action: ({ data, index }) => {
              return (
                <RenderIf condition={hasAccessEdit}>
                  <View style={styles.action} key={`action-${index}`}>
                    <TouchableOpacity
                      activeOpacity={0.8}
                      onPress={async () => await handleDelete(data)}>
                      <Image
                        style={{ width: 15, height: 18 }}
                        width={15}
                        height={18}
                        source={require('@assets/img/icons/trash-grey.svg')}
                        svg={true}
                      />
                    </TouchableOpacity>
                  </View>
                </RenderIf>
              );
            },
          }}
        />
      </RenderIf>

      <ModalFull
        show={showModalExport}
        title="Export Forecast Stock"
        noTranslate={true}
        onClose={handleCloseExportModal}>
        <View style={styles.sectionWrapper}>
          <SelectBox
            label="Export By"
            hideLabel
            hideSearch
            options={GROUP_BY_OPTIONS}
            value={selectedGroupBy}
            placeholder="Select Export Type"
            trackBy="value"
            labelKey="label"
            isDropdown={false}
            onChange={(value) => {
              setSelectedGroupBy(value);
            }}
          />
          <Button
            disabled={!selectedGroupBy}
            type="primary"
            rounded={true}
            buttonStyle={{
              marginTop: 10,
            }}
            title={
              <SociollaText
                type="medium"
                style={{ fontSize: 14, color: Colors.white }}>
                Export
              </SociollaText>
            }
            onPress={handleExportForecast}
          />
        </View>
      </ModalFull>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  sectionWrapper: { padding: 15 },
  tabContainer: {
    width: '100%',
    padding: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: Colors.white,
    borderRadius: 5,
    zIndex: 10,
    marginBottom: 20,
  },
  action: {
    alignItems: 'center',
  },
});

export default ForecastStockScreen;
